"use client";

/**
 * Overflow ("⋯") menu — the canonical home for secondary and diagnostic
 * actions that used to sit in page headers as a row of equal buttons.
 *
 * Keyboard and screen-reader behaviour is the reason this is a component and
 * not an inline popover: the pattern was already hand-rolled twice in this
 * repo (user menu, issue row actions) and each copy re-implemented a
 * different subset of it. This one does the whole menu contract —
 *
 *   - `aria-haspopup="menu"` + `aria-expanded` on the trigger;
 *   - roving focus: ↓/↑ move, Home/End jump, Escape closes and RETURNS
 *     focus to the trigger (without that, closing a menu strands keyboard
 *     users at the top of the document);
 *   - opening with ↓ or Enter focuses the first item;
 *   - outside click and Escape both close;
 *   - items render as real <a>/<button> elements so middle-click, ⌘-click
 *     and "copy link" keep working on navigational entries.
 *
 * Status is never carried by colour alone: a destructive item gets both the
 * danger tone AND its own separator group.
 */

import { useCallback, useEffect, useId, useRef, useState } from "react";
import Link from "next/link";
import { Check, MoreHorizontal, type LucideIcon } from "lucide-react";
import { cn } from "@/lib/cn";

export type ActionMenuItem = {
  id: string;
  label: string;
  icon?: LucideIcon;
  /** Navigational item. Mutually exclusive with `onSelect`. */
  href?: string;
  /** In-page item. Mutually exclusive with `href`. */
  onSelect?: () => void;
  disabled?: boolean;
  /** Renders in the danger tone and in its own group at the end. */
  destructive?: boolean;
  /** Marks the current choice (status / assignee pickers): a check mark,
   *  `aria-checked`, and the item renders as `menuitemradio`. */
  selected?: boolean;
  /** Secondary text after the label (a count, a shortcut hint). */
  hint?: string;
};

export type ActionMenuProps = {
  items: ActionMenuItem[];
  /** Accessible name for the trigger, e.g. "More project actions". */
  label: string;
  className?: string;
  "data-testid"?: string;
  /** A labelled trigger (e.g. the current status) instead of the "⋯"
   *  icon. Same menu contract either way. */
  trigger?: { content: React.ReactNode; className?: string };
  /** Which edge the menu aligns to. Default right. */
  align?: "left" | "right";
  /** Bump to open the menu from outside (a keyboard shortcut); opens with
   *  the first item focused. */
  openSignal?: number;
};

export function ActionMenu({
  items, label, className, "data-testid": testId, trigger, align = "right", openSignal,
}: ActionMenuProps) {
  const [open, setOpen] = useState(false);
  const [focusIndex, setFocusIndex] = useState(-1);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const itemRefs = useRef<Array<HTMLElement | null>>([]);
  const menuId = useId();

  const enabled = items.filter((i) => !i.disabled);
  const ordered = [
    ...enabled.filter((i) => !i.destructive),
    ...enabled.filter((i) => i.destructive),
  ];
  const firstDestructive = ordered.findIndex((i) => i.destructive);

  const close = useCallback((returnFocus: boolean) => {
    setOpen(false);
    setFocusIndex(-1);
    if (returnFocus) triggerRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!open) return;
    const onPointer = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) close(false);
    };
    // Escape is bound at the DOCUMENT, not just on the menu. A menu opened
    // with the mouse leaves focus on the trigger, so a handler scoped to the
    // menu never sees the key — the menu stayed open over the page and the
    // only way out was another click. Escape must always dismiss.
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") { e.stopPropagation(); close(true); }
    };
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, close]);

  // Opened from outside (keyboard shortcut): open with the first item
  // focused. Adjusted during render, keyed on the signal value.
  const [seenSignal, setSeenSignal] = useState(openSignal);
  if (openSignal !== seenSignal) {
    setSeenSignal(openSignal);
    if (openSignal) { setOpen(true); setFocusIndex(0); }
  }

  // Move DOM focus whenever the roving index changes, so the menu reads
  // correctly to a screen reader instead of only looking focused.
  useEffect(() => {
    if (!open || focusIndex < 0) return;
    itemRefs.current[focusIndex]?.focus();
  }, [open, focusIndex]);

  if (ordered.length === 0) return null;

  const onTriggerKey = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setOpen(true);
      setFocusIndex(0);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setOpen(true);
      setFocusIndex(ordered.length - 1);
    }
  };

  const onMenuKey = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case "Escape":
        // Handled by the document listener above; swallowed here so it does
        // not also reach a surrounding dialog.
        e.preventDefault();
        break;
      case "ArrowDown":
        e.preventDefault();
        setFocusIndex((i) => (i + 1) % ordered.length);
        break;
      case "ArrowUp":
        e.preventDefault();
        setFocusIndex((i) => (i - 1 + ordered.length) % ordered.length);
        break;
      case "Home":
        e.preventDefault();
        setFocusIndex(0);
        break;
      case "End":
        e.preventDefault();
        setFocusIndex(ordered.length - 1);
        break;
      case "Tab":
        // Tabbing out of an open menu closes it, matching every other menu
        // the operator meets in this app.
        close(false);
        break;
      default:
        break;
    }
  };

  const itemClass = (destructive?: boolean) =>
    cn(
      "w-full flex items-center gap-2 px-2.5 py-1.5 rounded-md text-[12.5px] text-left transition focus-ring",
      destructive
        ? "text-fg-dim hover:bg-status-critical/10 hover:text-status-critical focus:text-status-critical"
        : "text-fg-dim hover:bg-bg-hover hover:text-fg",
    );

  return (
    <div className={cn("relative", className)} ref={rootRef}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => (open ? close(false) : (setOpen(true), setFocusIndex(-1)))}
        onKeyDown={onTriggerKey}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-label={label}
        title={label}
        data-testid={testId}
        className={cn(
          trigger
            ? "h-7 px-2 inline-flex items-center gap-1.5 rounded-md text-[12px] text-fg-dim transition focus-ring"
            : "h-8 w-8 grid place-items-center rounded-lg border border-line-subtle text-fg-muted transition focus-ring",
          "hover:text-fg hover:bg-bg-hover",
          open && "bg-bg-hover text-fg",
          trigger?.className,
        )}
      >
        {trigger ? trigger.content : <MoreHorizontal size={15} />}
      </button>

      {open && (
        <div
          id={menuId}
          role="menu"
          aria-label={label}
          onKeyDown={onMenuKey}
          className={cn(
            "absolute mt-1 w-60 z-30 rounded-lg border border-line-subtle bg-bg-raised shadow-lg p-1",
            align === "left" ? "left-0" : "right-0",
          )}
        >
          {ordered.map((item, index) => {
            const Icon = item.icon;
            const body = (
              <>
                {Icon && <Icon size={13} className="shrink-0" />}
                <span className="truncate">{item.label}</span>
                {item.hint && <span className="ml-auto pl-2 text-[11px] text-fg-muted shrink-0">{item.hint}</span>}
                {item.selected && <Check size={13} className={cn("shrink-0 text-brand-300", !item.hint && "ml-auto")} aria-hidden />}
              </>
            );
            const shared = {
              role: (item.selected !== undefined ? "menuitemradio" : "menuitem") as "menuitem",
              "aria-checked": item.selected !== undefined ? item.selected : undefined,
              tabIndex: index === focusIndex ? 0 : -1,
              className: itemClass(item.destructive),
              onFocus: () => setFocusIndex(index),
            };
            return (
              <div key={item.id}>
                {index === firstDestructive && index > 0 && (
                  <div className="my-1 h-px bg-line-subtle" role="separator" />
                )}
                {item.href ? (
                  <Link
                    {...shared}
                    href={item.href}
                    ref={(el) => { itemRefs.current[index] = el; }}
                    onClick={() => close(false)}
                  >
                    {body}
                  </Link>
                ) : (
                  <button
                    {...shared}
                    type="button"
                    ref={(el) => { itemRefs.current[index] = el; }}
                    onClick={() => { close(false); item.onSelect?.(); }}
                  >
                    {body}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
